// LiveStreamBadge.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import Animator from '../component/Animator.jsx';
import {
  LIVESTREAM_TYPE_LIVE_BADGE,
  LIVESTREAM_TYPE_STATUS_BADGE,
  LIVESTREAM_TYPE_DOT,
} from '../resource/userStatusIndicatorConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import {
  PRESET_SD,
  privateShowStatus as privateShowStatusConstant,
} from '../resource/liveStreamConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { textColor } from '../style/variables.js';
import OpacityBreath from '../style/OpacityBreath.js';
import { formatTimeStringBySec } from '../resource/formatTimeString.js';
import LivestreamDotResource from '../../img/ic_livestream.gif';
import CrownResource from '../../img/ic_crown.svg';
import LivestreamPublicResource from '../../img/ic_private_view_xs.svg';

export class LiveStreamBadge extends React.PureComponent {
  getFormattedSeconds = ({ target }) => {
    const { timestampOffsetSeconds } = this.props;
    const now = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });
    return formatTimeStringBySec(Math.max(0, target - now));
  };
  render() {
    const {
      type,
      privateShowStatus,
      streamStatus,
      fundingExp,
      size,
      t,
      isOnUserGrid,
    } = this.props;
    if (LIVESTREAM_TYPE_DOT === type) {
      return (
        <LivestreamDot>
          <Dot src={LivestreamDotResource} size={size} />
        </LivestreamDot>
      );
    }
    if (type === LIVESTREAM_TYPE_LIVE_BADGE) {
      return <LiveTextBadge size={size}>LIVE</LiveTextBadge>;
    }
    if (type === LIVESTREAM_TYPE_STATUS_BADGE) {
      if (
        [
          privateShowStatusConstant.PREPARING,
          privateShowStatusConstant.PERFORMING,
        ].includes(privateShowStatus)
      ) {
        return (
          <ShowBadge size={size} isOnUserGrid={isOnUserGrid}>
            <PrivateShowIcon src={CrownResource} aria-hidden />
            {t('badge_live_performing', {
              ns: TranslationNamespace.LIVESTREAM,
            })}
          </ShowBadge>
        );
      }
      if (
        [
          privateShowStatusConstant.FUNDING,
          privateShowStatusConstant.FUNDING_FINAL_CALL,
        ].includes(privateShowStatus)
      ) {
        return (
          <ShowBadge isFunding size={size} isOnUserGrid={isOnUserGrid}>
            {t('badge_live_funding', {
              ns: TranslationNamespace.LIVESTREAM,
            })}
            <AnimatorWrapper>
              <Animator
                fps={1}
                render={() => this.getFormattedSeconds({ target: fundingExp })}
              />
            </AnimatorWrapper>
          </ShowBadge>
        );
      }
      if (streamStatus === PRESET_SD) {
        return (
          <PrivateBadge size={size}>
            <LivestreamPublicIcon src={LivestreamPublicResource} />
            {t('title_stream_private', {
              ns: TranslationNamespace.LIVESTREAM,
            })}
          </PrivateBadge>
        );
      }
      return (
        <LiveTextBadge size={size}>
          {t('badge_live_public', {
            ns: TranslationNamespace.LIVESTREAM,
          })}
        </LiveTextBadge>
      );
    }
    return null;
  }
}

LiveStreamBadge.propTypes = {
  type: PropTypes.string,
  privateShowStatus: PropTypes.string,
  streamStatus: PropTypes.string,
  timestampOffsetSeconds: PropTypes.number,
  fundingExp: PropTypes.number,
  size: PropTypes.number,
  isOnUserGrid: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

LiveStreamBadge.defaultProps = {
  type: LIVESTREAM_TYPE_DOT,
  privateShowStatus: null,
  streamStatus: null,
  isOnUserGrid: false,
  timestampOffsetSeconds: 0,
  fundingExp: 0,
  size: 0,
};

const LivestreamDot = styled.div`
  display: flex;
  align-items: center;
`;

const Dot = styled.img.attrs(({ size }) => ({
  width: `${size}px`,
  height: `${size}px`,
}))``;

const ShowBadge = styled.div.attrs(({ isFunding, size }) => ({
  style: {
    paddingLeft: `${isFunding ? 8 : 26}px`,
    height: `${size}px`,
  },
}))`
  ${({ isFunding }) => {
    if (!isFunding) {
      return css`
        ${OpacityBreath({ duration: 0.5 })};
      `;
    }
  }}
  position: relative;
  border-radius: 2px;
  padding: 0px 8px;
  display: inline-flex;
  align-items: center;
  background-image: ${({ isOnUserGrid }) =>
    isOnUserGrid
      ? 'linear-gradient(90deg, #fd5696 0%, #ffc100 100%)'
      : 'linear-gradient(102deg,rgb(164, 69, 179) 4%,rgb(212, 24, 114) 54%,rgb(255, 0, 102))'};
  font-size: 12px;
  color: ${textColor.white};
`;

const PrivateShowIcon = styled.img`
  position: absolute;
  top: 50%;
  left: 4px;
  width: 20px;
  height: 20px;
  transform: translateY(-50%);
`;

const AnimatorWrapper = styled.div`
  margin-left: 4px;
`;

const PrivateBadge = styled.div.attrs(({ size }) => ({
  style: {
    height: `${size}px`,
  },
}))`
  ${OpacityBreath({ duration: 0.5 })};
  border-radius: 2px;
  padding: 0px 4px;
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  background-image: linear-gradient(
    110deg,
    rgb(186, 57, 243),
    rgb(84, 32, 162) 102%
  );
`;

const LivestreamPublicIcon = styled.img`
  margin-right: 4px;
  width: 12px;
  height: 12px;
`;

const LiveTextBadge = styled.div.attrs(({ size }) => ({
  style: {
    lineHeight: `${size}px`,
  },
}))`
  border-radius: 2px;
  padding: 0px 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${textColor.white};
  white-space: nowrap;
  background-image: linear-gradient(
    117deg,
    rgb(164, 69, 179) 5%,
    rgb(212, 24, 114) 55%,
    rgb(255, 0, 102)
  );
`;

export default withTranslation()(LiveStreamBadge);
